import type { ConditionalAnswer, OperatingSchedule, PlotDevice, TimePeriod } from '../domain/types.js';
import type { ActorContext, HistoryContext, HistoryEventSlice, WorldContext } from './types.js';

export function formatLocation(world: WorldContext): string {
  const { locationType, location, connected } = world;
  const parts: string[] = [];
  if (locationType) parts.push(String(locationType));
  parts.push(location.name);
  if (location.region.trim()) parts.push(location.region.trim());
  if (location.language.trim()) parts.push(`lang=${location.language.trim()}`);
  if (connected?.locationType?.trim()) parts.push(`type: ${connected.locationType.trim()}`);
  if (connected?.description?.trim()) parts.push(`desc: ${connected.description.trim()}`);
  return parts.join(' · ');
}

export function formatPeriod(period: TimePeriod): string {
  const parts: string[] = [];
  if (period.present?.trim()) parts.push(`present: ${period.present.trim()}`);
  if (period.period?.trim()) parts.push(`period: ${period.period.trim()}`);
  const core = parts.length > 0 ? parts.join('; ') : '(unspecified)';
  const rationale = period.rationale?.trim();
  return rationale ? `${core} — rationale: ${rationale}` : core;
}

export function formatSchedule(schedule: OperatingSchedule): string {
  const days = schedule.daysOpen.join(',');
  const overnight = schedule.overnight ? ' overnight' : '';
  const tzLabel = schedule.timezone?.trim() || schedule.timezoneStrategy;
  return `hours=${schedule.hasOperatingHours} days=[${days}] ${schedule.openTime}-${schedule.closeTime}${overnight} closedBehavior=${schedule.closedBehavior} tz=${tzLabel}`;
}

export function formatAnswers(answers: Record<string, ConditionalAnswer>): string {
  return Object.entries(answers)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([id, a]) => `- ${id}: ${JSON.stringify(a.value)} (${a.status})`)
    .join('\n');
}

export function privacySafeName(firstName: string, lastInitial?: string): string {
  return lastInitial ? `${firstName} ${lastInitial}.` : firstName;
}

/** Strip full last names from a raw display label. */
export function sanitizeDisplayName(raw: string): string {
  const trimmed = raw.trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length <= 1) return trimmed;
  const first = parts[0] ?? trimmed;
  const second = parts[1] ?? '';
  if (second.endsWith('.')) return `${first} ${second}`;
  if (second.length === 1) return `${first} ${second}.`;
  return privacySafeName(first, second.charAt(0).toUpperCase());
}

export function formatAppearance(appearance?: Record<string, string>): string {
  if (!appearance || Object.keys(appearance).length === 0) return '(none)';
  return Object.entries(appearance)
    .map(([k, v]) => `${k}=${v}`)
    .join(', ');
}

export function formatActor(actor: ActorContext): string {
  const lines = [
    `- ${actor.displayName} [${actor.actorId}] (${actor.species}, ${actor.sex}, ${actor.ageRange})`,
  ];
  if (actor.appearance && Object.keys(actor.appearance).length > 0) {
    lines.push(`  appearance: ${formatAppearance(actor.appearance)}`);
  }
  if (actor.traits?.length) lines.push(`  traits: ${actor.traits.join(', ')}`);
  if (actor.goals?.length) lines.push(`  goals: ${actor.goals.join(', ')}`);
  if (actor.relationships?.length) {
    const rels = actor.relationships
      .map((r) => `${r.targetActorId}(fam=${r.familiarity},aff=${r.affinity},trust=${r.trust})`)
      .join('; ');
    lines.push(`  relationships: ${rels}`);
  }
  if (actor.durableMemories?.length) {
    const mems = actor.durableMemories
      .map((m) => `[${m.salience}] ${m.summary}`)
      .join('; ');
    lines.push(`  memories: ${mems}`);
  }
  return lines.join('\n');
}

export function formatActiveDevices(devices: PlotDevice[]): string {
  if (devices.length === 0) return '(none)';
  return devices
    .map((d) => {
      const trigger = d.trigger ?? 'ambient';
      const consequence = d.consequenceRange ?? 'minor';
      return `- [${d.deviceId}] ${d.title} (${d.category}) trigger=${trigger} consequence=${consequence}`;
    })
    .join('\n');
}

export function formatEvents(events: HistoryEventSlice[]): string {
  return events
    .map((e) => `- [${e.eventId}] ${e.occurredAt}: ${e.summary}`)
    .join('\n');
}

export function formatCompactSummaries(
  summaries: HistoryContext['compactSummaries'],
): string {
  if (summaries.length === 0) return '(none)';
  return summaries
    .map((s) => `- [${s.summaryId}] (${s.scope}): ${s.text}`)
    .join('\n');
}
